import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import PaymentUpdateForm from './PaymentUpdateForm';
import { SubscriptionInfo, PaymentSetupResponse } from './types';

// Remove the hardcoded stripe promise - we'll initialize it dynamically
// const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);

interface PublicSubscriptionPaymentUpdateProps {
  subscriptionId: string;
  token?: string; // Security token to validate access
}

const PublicSubscriptionPaymentUpdate: React.FC<PublicSubscriptionPaymentUpdateProps> = ({
  subscriptionId,
  token
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const initializePaymentUpdate = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Initializing public payment update for subscription:', subscriptionId);

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-subscription-payment-setup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
          },
          body: JSON.stringify({ 
            subscriptionId,
            token // Optional security token
          }),
        });

        const responseText = await response.text();
        console.log('Public setup response:', responseText);

        if (!response.ok) {
          let errorMessage = 'Failed to initialize payment method update';
          try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const data: PaymentSetupResponse = JSON.parse(responseText);
        if (!data.success || !data.client_secret || !data.subscription || !data.publishable_key || !data.stripe_account_id) {
          throw new Error(data.error || 'Invalid response from server');
        }

        console.log('Setup successful:', data.setup_intent_id);
        console.log('Using publishable key for account:', data.stripe_account_id);
        
        // Initialize Stripe with the correct publishable key and account
        const stripe = loadStripe(data.publishable_key, {
          stripeAccount: data.stripe_account_id
        });
        
        setStripePromise(stripe);
        setClientSecret(data.client_secret);
        setSubscriptionInfo(data.subscription);
        setStripeAccountId(data.stripe_account_id);
      } catch (err) {
        console.error('Setup error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment update';
        setError(errorMessage);
        toast({
          title: "Initialization Failed",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (subscriptionId) {
      initializePaymentUpdate();
    }
  }, [subscriptionId, token, toast]);

  const handleSuccess = () => {
    // Could redirect or show success message
    window.location.href = '/payment-method-update/success';
  };

  const handleCancel = () => {
    // Go back or close
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-20">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="w-full">
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
                <p className="text-gray-500">Loading subscription details...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !clientSecret || !subscriptionInfo || !stripePromise) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-20">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="w-full">
            <CardContent className="p-6">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {error || 'Failed to load subscription information'}
                </AlertDescription>
              </Alert>
              <div className="mt-4 flex justify-center">
                <Button variant="outline" onClick={handleCancel}>
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret: clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        spacingUnit: '4px',
        borderRadius: '6px',
      },
      rules: {
        '.Tab': {
          border: '1px solid #e6e6e6',
          boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)',
        },
        '.Tab:hover': {
          color: 'var(--colorText)',
        },
        '.Tab--selected': {
          borderColor: 'var(--colorPrimary)',
          boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02), 0 0 0 2px var(--colorPrimary)',
        },
        '.Input': {
          boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)',
        }
      },
    },
  };

  return (
    <Elements key={clientSecret} options={options} stripe={stripePromise}>
      <PaymentUpdateForm
        subscriptionId={subscriptionId}
        clientSecret={clientSecret}
        subscriptionInfo={subscriptionInfo}
        stripeAccountId={stripeAccountId}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </Elements>
  );
};

export default PublicSubscriptionPaymentUpdate;